'use client';

import Link from 'next/link';
import { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { CircleCheckBig, ArrowUpRight, ChevronDown, HeartHandshake, ShieldCheck, Globe } from 'lucide-react';
import GlobalTitle from '@shared/GlobalTitle';
import SectionShapes from "@shared/SectionShapes";

const ICONS = { HeartHandshake, ShieldCheck, Globe };

const accents = [
  { check: 'text-primary', icon: 'text-white/20' },
  { check: 'text-rose-400', icon: 'text-rose-400/20' },
  { check: 'text-rose-400', icon: 'text-rose-400/20' },
];

function AccordionCTAButton({ href, label, className = '' }) {
  return (
    <Link href={href} className={`w-fit ${className}`}>
      <Button className="bg-red-gradient border-0 text-sm py-4 md:py-5 px-4 btn-glass group cursor-pointer">
        <span className="relative z-10 flex items-center gap-2 text-white group-hover:text-secondary transition-colors duration-400">
          {label}
          <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
        </span>
      </Button>
    </Link>
  );
}

export default function VerticalAccordionSection({ sections = [] }) {
  const [active, setActive] = useState(0);
  const hoverTimer = useRef(null);

  const handleMouseEnter = (i) => {
    hoverTimer.current = setTimeout(() => setActive(i), 180);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer.current);
  };

  return (
    <section className="py-12 md:py-20 bg-[#f7f8fc] overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 relative">
        <SectionShapes variant="light" />

        {/* Mobile — stacked accordion, one section per row */}
        <div className="flex flex-col lg:hidden rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30 bg-ink divide-y divide-white/10">
          {sections.map((section, i) => {
            const isActive = i === active;
            const a = accents[i] ?? accents[0];
            const Icon = ICONS[section.icon];

            return (
              <div
                key={section.id}
                className={`transition-colors duration-300 ${isActive ? 'bg-white/8' : ''}`}
              >
                <button
                  type="button"
                  onClick={() => setActive(i)}
                  aria-expanded={isActive}
                  className="w-full flex items-center justify-between gap-3 p-5 text-left cursor-pointer"
                >
                  <span className="flex items-center gap-3 min-w-0">
                    {Icon && <Icon size={20} className={`${a.check} shrink-0`} />}
                    <span className="font-bold text-white text-sm tracking-[0.08em] uppercase truncate">
                      {section.titleText}
                    </span>
                  </span>
                  <ChevronDown
                    size={16}
                    className={`text-white/50 shrink-0 transition-transform duration-300 ${isActive ? 'rotate-180' : ''}`}
                  />
                </button>

                {isActive && (
                  <div className="px-5 pb-6">
                    <p className="text-white/70 mb-5">{section.description}</p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-2 gap-y-3">
                      {section.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-white/90 text-sm">
                          <CircleCheckBig size={13} className={`${a.check} mt-0.5 shrink-0`} />
                          {f}
                        </li>
                      ))}
                    </ul>
                    {section.url && (
                      <AccordionCTAButton href={section.url} label={section.buttonText} className="mt-6 block" />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop — horizontal vertical-tab accordion */}
        <div className="hidden lg:flex h-145 rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/30 bg-ink">

          {sections.map((section, i) => {
            const isActive = i === active;
            const a = accents[i] ?? accents[0];
            const Icon = ICONS[section.icon];

            return (
              <div
                key={section.id}
                className={`relative overflow-hidden transition-all duration-500 ease-in-out border-r border-white/10 last:border-r-0 ${isActive
                  ? 'flex-5 bg-white/8 backdrop-blur-lg saturate-150'
                  : 'flex-[0.6] cursor-pointer bg-white/5 backdrop-blur-xl hover:bg-white/8'
                  }`}
                onClick={() => setActive(i)}
                onMouseEnter={() => handleMouseEnter(i)}
                onMouseLeave={handleMouseLeave}
              >
                <div
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${isActive ? 'opacity-0 pointer-events-none' : 'opacity-100'
                    }`}
                >
                  <span className="[writing-mode:vertical-rl] rotate-180 font-bold text-white/40 text-sm tracking-[0.14em] uppercase whitespace-nowrap select-none">
                    {section.titleText}
                  </span>
                </div>

                {Icon && (
                  <Icon
                    size={180}
                    strokeWidth={0.65}
                    className={`absolute -top-1 -right-1 pointer-events-none select-none transition-opacity duration-300 ${a.icon} ${isActive ? 'opacity-20' : 'opacity-0'
                      }`}
                  />
                )}

                {/* Active — content */}
                <div
                  className={`absolute inset-0 flex flex-col justify-center p-10 transition-opacity duration-400 ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                >
                  <GlobalTitle
                    topText={section.eyebrow}
                    titleText={section.titleText}
                    coloredText={section.coloredText}
                    classes="text-white text-left !text-3xl lg:!text-4xl !mb-6"
                  />
                  <p className="text-white/70 mb-8 max-w-2xl">
                    {section.description}
                  </p>
                  <ul className="grid grid-cols-2 gap-x-2 gap-y-3">
                    {section.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-white/90">
                        <CircleCheckBig size={13} className={`${a.check} mt-0.5 shrink-0`} />
                        {f}
                      </li>
                    ))}
                  </ul>
                  {section.url && (
                    <AccordionCTAButton href={section.url} label={section.buttonText} className="mt-6" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}